import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import {
  Check,
  X,
  ArrowRight,
  ChevronRight,
  Gift,
  Zap,
  Shield,
  Ruler,
  FileText,
  Users,
  Palette,
  Star,
  MapPin,
} from 'lucide-react';

export default function FreeAlternative() {
  const navigate = useNavigate();
  const { isAuthenticated, openAuthModal } = useAuth();

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      openAuthModal('register');
    }
  };

  const freeFeatures = [
    { feature: 'Satellite Roof Measurements', free: true, pro: true },
    { feature: 'Measurements Per Month', free: '3', pro: 'Unlimited' },
    { feature: 'Roof Area (Squares)', free: true, pro: true },
    { feature: 'Pitch Calculation', free: true, pro: true },
    { feature: 'Address Search', free: true, pro: true },
    { feature: 'PDF Reports', free: false, pro: true },
    { feature: 'Material Estimates', free: false, pro: true },
    { feature: 'Gutter Measurements', free: false, pro: true },
    { feature: 'Team Members', free: false, pro: 'Up to 5' },
    { feature: 'White-Label Branding', free: false, pro: '$79/mo' },
    { feature: 'Priority Support', free: false, pro: true },
  ];

  const comparisonData = [
    { feature: 'Monthly Cost', free: '$0', pro: '$19/mo', eagleview: '$40-75/report' },
    { feature: 'Measurements', free: '3/month', pro: 'Unlimited', eagleview: 'Per-report pricing' },
    { feature: 'Turnaround Time', free: 'Instant', pro: 'Instant', eagleview: '24-48 hours' },
    { feature: 'PDF Reports', free: false, pro: true, eagleview: true },
    { feature: 'No Credit Card Needed', free: true, pro: false, eagleview: false },
    { feature: 'No Long-Term Contract', free: true, pro: true, eagleview: false },
    { feature: 'Cancel Anytime', free: 'N/A', pro: true, eagleview: false },
    { feature: 'Cost for 10 Reports/Month', free: '$0 (3 max)', pro: '$19', eagleview: '$400-750' },
  ];

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'SquareCount Free Roof Measurement Software',
    description: 'Free satellite roof measurement software for roofing contractors. Get 3 free measurements per month with no credit card required. Upgrade to Pro for unlimited reports at $19/mo.',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: 'https://squarecount.io/free-alternative',
    offers: [
      {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: 'Free tier: 3 satellite roof measurements per month',
      },
      {
        '@type': 'Offer',
        price: '19.00',
        priceCurrency: 'USD',
        description: 'Pro plan: Unlimited measurements with PDF reports',
      },
    ],
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>Free Roof Measurement Software | 3 Free Reports/Month | SquareCount</title>
        <meta
          name="description"
          content="Free roof measurement software for contractors. Get 3 free satellite roof measurements per month — no credit card required. Free roofing estimator with area, pitch, and material estimates. Upgrade to Pro for $19/mo."
        />
        <meta property="og:title" content="Free Roof Measurement Software | SquareCount" />
        <meta
          property="og:description"
          content="Free satellite roof measurements for contractors. 3 reports/month free, no credit card. Pro plan: $19/mo unlimited."
        />
        <meta name="keywords" content="free roof measurement, free roofing estimator, free roof software, free roof measurement tool, free satellite roof measurement, free roof measurement software" />
        <link rel="canonical" href="https://squarecount.io/free-alternative" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23f97316\' fill-opacity=\'0.15\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />

        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-4 py-2 mb-6">
              <Gift className="w-4 h-4 text-green-400" />
              <span className="text-green-300 text-sm font-medium">No credit card required</span>
            </div>

            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-tight mb-6">
              <span className="text-orange-500">Free</span> Roof Measurement Software
              &amp; Roofing Estimator — No Credit Card
            </h1>

            <p className="text-xl text-slate-300 mb-8 max-w-2xl">
              SquareCount gives roofing contractors 3 free satellite roof measurements
              every month. Get roof area, pitch, and basic estimates — completely free.
              Upgrade to Pro when you need more.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleGetStarted}
                className="bg-orange-500 hover:bg-orange-400 text-white h-14 px-8 font-heading font-bold tracking-wide text-lg btn-tactile"
              >
                Start Your 7-Day Contractor Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/pricing')}
                className="h-14 px-8 font-heading font-bold tracking-wide text-lg border-2 border-white/20 text-white hover:bg-white/10 hover:border-white/40 bg-transparent"
              >
                Compare Plans
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get Free */}
      <section className="py-16 border-t border-slate-800/50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-3">
              What You Get — Completely Free
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              No trials. No expiration. Just free roof measurements every month.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Ruler,
                title: '3 Measurements/Month',
                desc: 'Measure up to 3 roofs per month using satellite imagery. Resets every billing cycle.',
              },
              {
                icon: MapPin,
                title: 'Address Search',
                desc: 'Search any US address and get instant satellite-based roof measurements.',
              },
              {
                icon: Star,
                title: 'Roof Area & Pitch',
                desc: 'Get total roof area in squares, pitch factor, and basic roof geometry for every measurement.',
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="p-6 bg-slate-900/50 border border-slate-800 rounded-lg">
                  <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-green-400" />
                  </div>
                  <h3 className="font-heading font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Free vs Pro Table */}
      <section className="py-16 border-t border-slate-800/50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-3">
              Free vs Pro — What's the Difference?
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Free gets you started. Pro unlocks everything.
            </p>
          </div>

          <div className="bg-slate-900/50 border border-slate-800 rounded-lg overflow-hidden">
            <div className="grid grid-cols-3 gap-4 p-4 bg-slate-800/80 border-b border-slate-700">
              <div className="text-slate-400 font-medium text-sm">Feature</div>
              <div className="text-center">
                <span className="text-green-400 font-heading font-bold text-sm">Free</span>
                <p className="text-slate-500 text-xs">$0/mo</p>
              </div>
              <div className="text-center">
                <span className="text-orange-500 font-heading font-bold text-sm">Pro</span>
                <p className="text-slate-500 text-xs">$19/mo</p>
              </div>
            </div>

            {freeFeatures.map((row) => (
              <div
                key={row.feature}
                className="grid grid-cols-3 gap-4 p-4 border-b border-slate-800/50 last:border-0"
              >
                <div className="text-slate-300 text-sm">{row.feature}</div>
                {['free', 'pro'].map((col) => (
                  <div key={col} className="text-center">
                    {typeof row[col] === 'boolean' ? (
                      row[col] ? (
                        <Check className="w-5 h-5 text-green-500 mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-slate-600 mx-auto" />
                      )
                    ) : (
                      <span className={`text-sm font-medium ${col === 'pro' ? 'text-orange-400' : 'text-slate-300'}`}>
                        {row[col]}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="mt-6 text-center">
            <Button
              onClick={handleGetStarted}
              className="bg-orange-500 hover:bg-orange-400 text-white h-12 px-8 font-heading font-bold tracking-wide"
            >
              Start Your 7-Day Contractor Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* SquareCount Free vs Pro vs EagleView */}
      <section className="py-16 border-t border-slate-800/50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-3">
              Free vs Pro vs EagleView
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Even our free tier beats the competition. Our Pro plan makes it a no-brainer.
            </p>
          </div>

          <div className="bg-slate-900/50 border border-slate-800 rounded-lg overflow-hidden">
            <div className="grid grid-cols-4 gap-4 p-4 bg-slate-800/80 border-b border-slate-700">
              <div className="text-slate-400 font-medium text-sm">Feature</div>
              <div className="text-center">
                <span className="text-green-400 font-heading font-bold text-sm">Free</span>
              </div>
              <div className="text-center">
                <span className="text-orange-500 font-heading font-bold text-sm">Pro $19/mo</span>
              </div>
              <div className="text-center">
                <span className="text-slate-500 font-heading font-bold text-sm">EagleView</span>
              </div>
            </div>

            {comparisonData.map((row) => (
              <div
                key={row.feature}
                className="grid grid-cols-4 gap-4 p-4 border-b border-slate-800/50 last:border-0"
              >
                <div className="text-slate-300 text-sm">{row.feature}</div>
                {['free', 'pro', 'eagleview'].map((col) => (
                  <div key={col} className="text-center">
                    {typeof row[col] === 'boolean' ? (
                      row[col] ? (
                        <Check className="w-5 h-5 text-green-500 mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-red-500 mx-auto" />
                      )
                    ) : (
                      <span
                        className={`text-sm ${
                          col === 'pro' ? 'text-orange-400 font-medium' : col === 'free' ? 'text-green-400' : 'text-slate-400'
                        }`}
                      >
                        {row[col]}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pro Unlocks */}
      <section className="py-16 border-t border-slate-800/50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-3">
              Your Free 7-Day Contractor Trial Unlocks Everything
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Every new account starts with 7 days of the full Contractor plan — here's what's inside.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: 'Unlimited Measurements', desc: 'No more counting. Measure as many roofs as you need.' },
              { icon: FileText, title: 'PDF Reports', desc: 'Professional, branded PDF reports ready to send to homeowners.' },
              { icon: Ruler, title: 'Editable Takeoffs & Job Costing', desc: 'Shingle, tile & metal lists, valley/ridge, custom materials, and a built-in cost estimator.' },
              { icon: Users, title: 'Team Access', desc: 'Add your crew — everyone measures under one account.' },
              { icon: Palette, title: 'White-Label Branding', desc: 'Put your company logo on every report you generate.' },
              { icon: Shield, title: 'Shareable Client Reports', desc: 'Send clients a live report link, plus CSV exports for your workflow.' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="p-6 bg-slate-900/50 border border-slate-800 rounded-lg hover:border-orange-500/30 transition-colors">
                  <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-orange-400" />
                  </div>
                  <h3 className="font-heading font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 border-t border-slate-800/50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
            Start Your 7-Day Contractor Trial
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            Get 3 free satellite roof measurements every month — no credit card, no expiration.
            Plus 7 days of full Contractor access on every new account. Keep it when you're ready.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleGetStarted}
              className="bg-orange-500 hover:bg-orange-400 text-white h-14 px-10 font-heading font-bold tracking-wide text-lg btn-tactile"
            >
              Start Your 7-Day Contractor Trial
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/pricing')}
              className="h-14 px-10 font-heading font-bold tracking-wide text-lg border-2 border-white/20 text-white hover:bg-white/10 hover:border-white/40 bg-transparent"
            >
              Compare Plans &amp; Pricing
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Is SquareCount really free?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. SquareCount offers a completely free tier with 3 satellite roof measurements per month. No credit card required, no trial period, and no expiration. You can use the free plan indefinitely."
            }
          },
          {
            "@type": "Question",
            "name": "What's included in the free plan?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The free plan includes 3 satellite roof measurements per month, roof area in squares, pitch calculation, and address search. PDF reports, material estimates, and gutter measurements require the Pro plan ($19/mo); report editing, job costing, branding, and team access are part of the Contractor plan ($79/mo). Every new account starts with a free 7-day Contractor trial."
            }
          },
          {
            "@type": "Question",
            "name": "How does SquareCount free compare to EagleView?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "SquareCount's free tier gives you 3 instant roof measurements per month at $0. EagleView charges $40-75 per report with 24-48 hour turnaround and requires contracts. Even SquareCount's Pro plan ($19/mo unlimited) is 90% cheaper than EagleView."
            }
          }
        ]
      }) }} />

      <Footer />
    </div>
  );
}
